"use client";

import { Coordinates } from "./types";
import { calculateDistance as calculateHaversineDistance } from "./locationData";
import { calculateDistanceMatrix } from "./googleMapsDistance";

// Cache for storing distance calculations to avoid excessive API calls
interface DistanceCache {
  [key: string]: {
    distance: number;
    timestamp: number;
    source: "google" | "haversine";
  };
}

class EnhancedDistanceCalculator {
  private cache: DistanceCache = {};
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
  private readonly MAX_CACHE_SIZE = 1000;
  private readonly STORAGE_KEY = "sio-distance-cache";
  private readonly PERSIST_DEBOUNCE_MS = 800;
  private _storageLoaded = false;
  private _persistTimeout: ReturnType<typeof setTimeout> | null = null;
  private isGoogleMapsAvailable: boolean | null = null;
  private googleMapsCheckTimestamp: number = 0;
  private readonly GOOGLE_MAPS_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private googleMapsAvailabilityPromise: Promise<boolean> | null = null;
  private readonly instanceId = Math.random().toString(36).slice(2);

  // Prevent duplicate concurrent distance-matrix computations for the same
  // origin + ordered uncached destinations + mode.
  private inFlightDistances: Map<string, Promise<number[]>> = new Map();

  // Global in-flight promise to prevent duplicate availability checks
  // even if multiple module instances or HMR copies exist.
  private static readonly GLOBAL_GOOGLE_AVAILABILITY_PROMISE_KEY =
    "__sio_googleMapsAvailabilityPromise__";

  private isBrowser(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.sessionStorage !== "undefined"
    );
  }

  /**
   * Load cache from sessionStorage (non-expired entries only). Safe to call on every reload.
   */
  private loadCacheFromStorage(): void {
    if (!this.isBrowser()) return;
    this._storageLoaded = true;
    try {
      const raw = window.sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as DistanceCache;
      const now = Date.now();
      Object.entries(parsed).forEach(([key, entry]) => {
        if (
          entry &&
          typeof entry.distance === "number" &&
          entry.timestamp &&
          now - entry.timestamp < this.CACHE_DURATION
        ) {
          this.cache[key] = {
            distance: entry.distance,
            timestamp: entry.timestamp,
            source: entry.source,
          };
        }
      });
    } catch {
      // Ignore parse or storage errors
    }
  }

  /**
   * Persist non-expired cache entries to sessionStorage. Debounced to avoid excessive writes.
   */
  private schedulePersist(): void {
    if (!this.isBrowser()) return;
    if (this._persistTimeout) clearTimeout(this._persistTimeout);
    this._persistTimeout = setTimeout(() => {
      this._persistTimeout = null;
      this.persistCacheToStorage();
    }, this.PERSIST_DEBOUNCE_MS);
  }

  private persistCacheToStorage(): void {
    if (!this.isBrowser()) return;
    try {
      const now = Date.now();
      const toStore: DistanceCache = {};
      Object.entries(this.cache).forEach(([key, entry]) => {
        if (entry && now - entry.timestamp < this.CACHE_DURATION) {
          toStore[key] = entry;
        }
      });
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(toStore));
    } catch {
      // Ignore storage full or other errors
    }
  }

  private ensureStorageLoaded(): void {
    if (!this._storageLoaded) {
      this.loadCacheFromStorage();
    }
  }

  /**
   * Generate a cache key for two coordinates
   */
  private generateCacheKey(coords1: Coordinates, coords2: Coordinates): string {
    // Round coordinates to reduce cache fragmentation while maintaining accuracy
    const lat1 = Math.round(coords1.lat * 10000) / 10000;
    const lng1 = Math.round(coords1.lng * 10000) / 10000;
    const lat2 = Math.round(coords2.lat * 10000) / 10000;
    const lng2 = Math.round(coords2.lng * 10000) / 10000;

    // Ensure consistent ordering for cache key
    const [first, second] =
      lat1 < lat2 || (lat1 === lat2 && lng1 < lng2)
        ? [`${lat1},${lng1}`, `${lat2},${lng2}`]
        : [`${lat2},${lng2}`, `${lat1},${lng1}`];

    return `${first}_${second}`;
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    const entries = Object.entries(this.cache);

    // If cache is getting too large, remove oldest entries
    if (entries.length > this.MAX_CACHE_SIZE) {
      const sorted = entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = sorted.slice(
        0,
        entries.length - this.MAX_CACHE_SIZE + 100,
      );
      toRemove.forEach(([key]) => delete this.cache[key]);
    }

    // Remove expired entries
    Object.keys(this.cache).forEach((key) => {
      if (now - this.cache[key].timestamp > this.CACHE_DURATION) {
        delete this.cache[key];
      }
    });
  }

  /**
   * Check if Google Maps is available and working
   */
  private async checkGoogleMapsAvailability(): Promise<boolean> {
    const now = Date.now();

    // Use cached result if recent
    if (
      this.isGoogleMapsAvailable !== null &&
      now - this.googleMapsCheckTimestamp < this.GOOGLE_MAPS_CHECK_INTERVAL
    ) {
      return this.isGoogleMapsAvailable;
    }

    const globalKey =
      EnhancedDistanceCalculator.GLOBAL_GOOGLE_AVAILABILITY_PROMISE_KEY;
    const globalPromise = (globalThis as unknown as Record<string, unknown>)[
      globalKey
    ] as Promise<boolean> | undefined;
    if (globalPromise) {
      return globalPromise;
    }

    // Set a global in-flight promise immediately (before any awaits)
    const globalInFlightPromise = (async (): Promise<boolean> => {
      try {
        // Avoid a separate availability probe that triggers an extra distance-matrix
        // request (this was contributing to the observed burst of POSTs).
        // If Google Maps is actually unavailable, the real distance requests will
        // fail and we already fall back to Haversine in calculateDistance/calculateDistances.
        this.isGoogleMapsAvailable = true;
        this.googleMapsCheckTimestamp = now;

        return this.isGoogleMapsAvailable;
      } catch (error) {
        console.warn(
          "[DISTANCE] Google Maps availability check failed:",
          error,
        );
        this.isGoogleMapsAvailable = false;
        this.googleMapsCheckTimestamp = now;
        return false;
      } finally {
        // Clear the global promise when finished so future checks can run.
        (globalThis as unknown as Record<string, unknown>)[globalKey] =
          undefined;
      }
    })();

    (globalThis as unknown as Record<string, unknown>)[globalKey] =
      globalInFlightPromise;

    // Also mirror into instance promise so later code paths still use it.
    this.googleMapsAvailabilityPromise = globalInFlightPromise;

    // If a check is already in-flight, reuse it to prevent duplicate calls
    // If a check is already in-flight, reuse it to prevent duplicate calls
    if (this.googleMapsAvailabilityPromise) {
      return this.googleMapsAvailabilityPromise;
    }

    try {
      return await this.googleMapsAvailabilityPromise;
    } finally {
      // Always clear the in-flight promise after it resolves/rejects
      this.googleMapsAvailabilityPromise = null;
    }
  }

  /**
   * Calculate distance using Google Maps Distance Matrix API with fallback to Haversine
   */
  async calculateDistance(
    coords1: Coordinates,
    coords2: Coordinates,
    mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
    forceHaversine = false,
  ): Promise<number> {
    // Input validation
    if (
      !coords1 ||
      !coords2 ||
      typeof coords1.lat !== "number" ||
      typeof coords1.lng !== "number" ||
      typeof coords2.lat !== "number" ||
      typeof coords2.lng !== "number"
    ) {
      console.warn("[DISTANCE] Invalid coordinates provided:", {
        coords1,
        coords2,
      });
      return 0;
    }

    const cacheKey = this.generateCacheKey(coords1, coords2);

    this.ensureStorageLoaded();

    // Check cache first
    const cached = this.cache[cacheKey];
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.distance;
    }

    // If forced to use Haversine or Google Maps check fails, use local calculation
    if (forceHaversine || !(await this.checkGoogleMapsAvailability())) {
      const distance = calculateHaversineDistance(coords1, coords2);

      // Cache the result
      this.cache[cacheKey] = {
        distance,
        timestamp: Date.now(),
        source: "haversine",
      };
      this.schedulePersist();

      return distance;
    }

    // Try Google Maps Distance Matrix API
    try {
      const result = await calculateDistanceMatrix([coords1], [coords2], mode);

      if (
        result.status === "OK" &&
        result.results.length > 0 &&
        result.results[0].status === "OK"
      ) {
        const distance = result.results[0].distance.value / 1000; // Convert to kilometers

        // Cache the result
        this.cache[cacheKey] = {
          distance,
          timestamp: Date.now(),
          source: "google",
        };
        this.schedulePersist();

        // Clean up cache periodically
        if (Math.random() < 0.1) {
          // 10% chance to clean up
          this.cleanupCache();
        }

        return distance;
      } else {
        console.warn("[DISTANCE] Google Maps API returned error:", {
          status: result.status,
          resultStatus: result.results[0]?.status,
        });
        throw new Error(`Google Maps API error: ${result.status}`);
      }
    } catch (error) {
      console.warn(
        "[DISTANCE] Google Maps Distance Matrix failed, falling back to Haversine:",
        error,
      );

      // Fallback to Haversine formula
      const distance = calculateHaversineDistance(coords1, coords2);

      // Cache the result
      this.cache[cacheKey] = {
        distance,
        timestamp: Date.now(),
        source: "haversine",
      };
      this.schedulePersist();

      return distance;
    }
  }

  /**
   * Calculate distances for multiple destination points from a single origin
   * More efficient for batch calculations
   * Uses request batching to combine multiple calls that arrive close together
   */
  async calculateDistances(
    origin: Coordinates,
    destinations: Coordinates[],
    mode: "driving" | "walking" | "bicycling" | "transit" = "driving",
    forceHaversine = false,
  ): Promise<number[]> {
    if (!origin || !destinations || destinations.length === 0) {
      return [];
    }

    this.ensureStorageLoaded();

    // If forced to use Haversine or Google Maps is not available, calculate all locally
    if (forceHaversine || !(await this.checkGoogleMapsAvailability())) {
      return destinations.map((dest) =>
        calculateHaversineDistance(origin, dest),
      );
    }

    // Check cache first and separate cached vs uncached destinations
    const results: (number | null)[] = new Array(destinations.length).fill(
      null,
    );
    const uncachedIndices: number[] = [];
    const uncachedDestinations: Coordinates[] = [];

    destinations.forEach((dest, index) => {
      const cacheKey = this.generateCacheKey(origin, dest);
      const cached = this.cache[cacheKey];

      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        results[index] = cached.distance;
      } else {
        uncachedIndices.push(index);
        uncachedDestinations.push(dest);
      }
    });

    // If all results are cached, return them
    if (uncachedDestinations.length === 0) {
      return results as number[];
    }

    try {
      const originKey = `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}`;
      const destKey = uncachedDestinations
        .map((d) => `${d.lat.toFixed(4)},${d.lng.toFixed(4)}`)
        .join("|");
      const inFlightKey = `dist:${mode}:${originKey}:${destKey}`;

      const inFlightPromise = this.inFlightDistances.get(inFlightKey);

      const computePromise =
        inFlightPromise ??
        (async (): Promise<number[]> => {
          // Calculate uncached distances using Google Maps
          // This will use the batcher which handles chunking automatically
          const result = await calculateDistanceMatrix(
            [origin],
            uncachedDestinations,
            mode,
          );

          const computed: number[] = new Array(uncachedDestinations.length)
            .fill(0)
            .map((_, i) => {
              const dest = uncachedDestinations[i];
              const res = result.results[i];

              const value = res?.distance?.value;
              if (result.status === "OK" && res?.status === "OK" && value) {
                const distance = value / 1000; // km
                // Cache each computed destination
                const cacheKey = this.generateCacheKey(origin, dest);
                this.cache[cacheKey] = {
                  distance,
                  timestamp: Date.now(),
                  source: "google",
                };
                this.schedulePersist();
                return distance;
              }

              // Fallback to Haversine for failed individual results
              const distance = calculateHaversineDistance(origin, dest);
              const cacheKey = this.generateCacheKey(origin, dest);
              this.cache[cacheKey] = {
                distance,
                timestamp: Date.now(),
                source: "haversine",
              };
              this.schedulePersist();
              return distance;
            });

          if (result.status !== "OK") {
            // If batch-level status failed, ensure computed is still Haversine-based.
            // (Computed above will have already done Haversine for failed results.)
          }

          return computed;
        })();

      if (!inFlightPromise) {
        this.inFlightDistances.set(inFlightKey, computePromise);
      }

      const uncachedDistances = await computePromise;

      // Clean up in-flight promise entry
      this.inFlightDistances.delete(inFlightKey);

      // Populate original `results[]` array in the right indices order
      uncachedIndices.forEach((originalIndex, i) => {
        results[originalIndex] = uncachedDistances[i];
      });
    } catch (error) {
      console.warn(
        "[DISTANCE] Google Maps batch calculation failed, using Haversine for uncached:",
        error,
      );

      // Ensure failed in-flight entries don't block future attempts.
      // (Safe even if another caller already deleted it.)
      try {
        const originKey = `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}`;
        const destKey = uncachedDestinations
          .map((d) => `${d.lat.toFixed(4)},${d.lng.toFixed(4)}`)
          .join("|");
        const inFlightKey = `dist:${mode}:${originKey}:${destKey}`;
        this.inFlightDistances.delete(inFlightKey);
      } catch {
        // Ignore cleanup errors
      }

      // Fallback to Haversine for all uncached destinations
      uncachedIndices.forEach((originalIndex, i) => {
        const dest = uncachedDestinations[i];
        const distance = calculateHaversineDistance(origin, dest);
        results[originalIndex] = distance;

        const cacheKey = this.generateCacheKey(origin, dest);
        this.cache[cacheKey] = {
          distance,
          timestamp: Date.now(),
          source: "haversine",
        };
        this.schedulePersist();
      });
    }

    // Clean up cache periodically
    if (Math.random() < 0.1) {
      this.cleanupCache();
    }

    return results as number[];
  }

  /**
   * Clear the entire cache (memory and sessionStorage)
   */
  clearCache(): void {
    this.cache = {};
    this.isGoogleMapsAvailable = null;
    if (this._persistTimeout) {
      clearTimeout(this._persistTimeout);
      this._persistTimeout = null;
    }
    if (this.isBrowser()) {
      try {
        window.sessionStorage.removeItem(this.STORAGE_KEY);
      } catch {
        // Ignore
      }
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    const entries = Object.values(this.cache);
    const googleEntries = entries.filter((e) => e.source === "google").length;
    const haversineEntries = entries.filter(
      (e) => e.source === "haversine",
    ).length;

    return {
      totalEntries: entries.length,
      googleEntries,
      haversineEntries,
      isGoogleMapsAvailable: this.isGoogleMapsAvailable,
      lastGoogleMapsCheck: this.googleMapsCheckTimestamp,
    };
  }
}

// Export singleton instance
export const enhancedDistanceCalculator = new EnhancedDistanceCalculator();

// Export the class for testing
export { EnhancedDistanceCalculator };
